import { useState } from 'react';
import type { FormEvent } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useChangeOwnPassword } from '../../api/queries';
import { PasswordField } from './PasswordField';

export type ChangePasswordDialogProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Authenticated self-service password change. Shown from the AppShell
 * user menu. Posts to /api/v1/me/change-password (full token required).
 *
 * Includes a "suggest a strong password" generator with a copy button —
 * the user can paste it into a password manager before submitting.
 */
export function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [success, setSuccess] = useState(false);
  const change = useChangeOwnPassword();

  const reset = () => {
    setCurrent(''); setNext(''); setConfirmPwd('');
    setSuccess(false);
    change.reset();
  };

  const onSuggest = () => {
    const pwd = generateStrongPassword(16);
    setNext(pwd);
    setConfirmPwd(pwd);
  };

  const onCopy = () => {
    if (next) navigator.clipboard.writeText(next).catch(() => { /* ignore */ });
  };

  const error = change.error as { message?: string } | null;
  const busy  = change.isPending;
  const okToSubmit = current.length > 0 && next.length > 0 && next === confirmPwd && !success;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await change.mutateAsync({ currentPassword: current, newPassword: next });
    setSuccess(true);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : () => { reset(); onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Change password
        <IconButton size="small" onClick={() => { reset(); onClose(); }} disabled={busy}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent dividers>
          <Stack spacing={2}>
            {success ? (
              <Alert severity="success">Password changed. Use the new password the next time you sign in.</Alert>
            ) : (
              <>
                {error?.message && <ErrorAlert>{error.message}</ErrorAlert>}
                <PasswordField
                  label="Current password"
                  autoFocus
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  required fullWidth disabled={busy}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <PasswordField
                    label="New password"
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    required fullWidth disabled={busy}
                    helperText="Subject to the platform's password policy."
                  />
                  <Tooltip title="Suggest a strong password">
                    <span>
                      <IconButton onClick={onSuggest} disabled={busy} aria-label="Suggest a strong password">
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Copy the new password">
                    <span>
                      <IconButton onClick={onCopy} disabled={busy || !next} aria-label="Copy the new password">
                        <ContentCopyIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <PasswordField
                  label="Confirm new password"
                  autoComplete="new-password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  required fullWidth disabled={busy}
                  error={confirmPwd.length > 0 && next !== confirmPwd}
                  helperText={confirmPwd.length > 0 && next !== confirmPwd ? 'Passwords do not match.' : ' '}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { reset(); onClose(); }} disabled={busy}>
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button
              type="submit"
              variant="contained"
              disabled={busy || !okToSubmit}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {busy ? 'Saving…' : 'Change password'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

// Inline strong-password generator used by the "suggest" button.
// 16 chars, mix of upper/lower/digit/special, ambiguous glyphs excluded.
function generateStrongPassword(length = 16): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digit = '23456789';
  const symbol = '!@#$%^&*-_+=?';
  const all = upper + lower + digit + symbol;
  const pick = (pool: string) => pool[Math.floor(Math.random() * pool.length)];
  const chars: string[] = [pick(upper), pick(lower), pick(digit), pick(symbol)];
  for (let i = 4; i < length; i++) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}
